import { FunctionComponent, ReactNode, useEffect, useMemo } from 'react';

import { MsalProvider } from '@azure/msal-react';
import { useLocation } from 'react-router-dom';

import { useDataStream, useInitializationEffect } from 'core/hooks';

import { SHARE_TOKEN_SESSION_STORAGE_KEY } from '../constants';
import { AuthenticationContext, ParsedShareAccessTokenContext } from '../contexts';
import { globalAuthenticationManager } from '../globals';
import { AuthenticationContextType } from '../types/AuthenticationContextType';

export const AuthenticationProvider: FunctionComponent<{
  children?: ReactNode;
}> = ({ children }) => {
  const location = useLocation();
  const activeScheme = useDataStream(globalAuthenticationManager.hub.activeScheme);
  const parsedShareToken = useDataStream(globalAuthenticationManager.shareScheme.hub.parsedShareToken);

  useInitializationEffect(() => {
    globalAuthenticationManager.initialize();
  });

  // Clean up share tokens when navigating away from share routes
  useEffect(() => {
    const currentPath = location.pathname;
    const isOnShareRoute = currentPath.startsWith('/share/');

    // If we're not on a share route and there's a share token, clear it
    if (!isOnShareRoute) {
      const shareToken = sessionStorage.getItem(SHARE_TOKEN_SESSION_STORAGE_KEY);
      if (shareToken) {
        console.log('AuthenticationProvider: Clearing share token on non-share route', {
          currentPath,
        });
        sessionStorage.removeItem(SHARE_TOKEN_SESSION_STORAGE_KEY);

        // Force a page reload to reinitialize authentication without the share token
        // This is necessary because the authentication system has already initialized
        window.location.reload();
      }
    }
  }, [location.pathname]);

  const newContext: AuthenticationContextType = useMemo(
    () => ({
      pca: globalAuthenticationManager.oidcScheme.pca,
      loginShare: globalAuthenticationManager.shareScheme.login,
      logoutShare: globalAuthenticationManager.shareScheme.logout,
      loginOidc: globalAuthenticationManager.oidcScheme.login,
      logoutOidc: globalAuthenticationManager.oidcScheme.logout,
      frontChannelLogoutOidc: globalAuthenticationManager.frontChannelLogoutOidc,
      oidcScheme: globalAuthenticationManager.oidcScheme,
      activeScheme,
    }),
    [activeScheme],
  );

  return (
    <AuthenticationContext.Provider value={newContext}>
      <ParsedShareAccessTokenContext.Provider value={parsedShareToken}>
        <MsalProvider instance={globalAuthenticationManager.oidcScheme.pca}>{children}</MsalProvider>
      </ParsedShareAccessTokenContext.Provider>
    </AuthenticationContext.Provider>
  );
};

AuthenticationProvider.displayName = 'AuthenticationProvider';
